import { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { ArrowUp, ArrowDown } from "lucide-react";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { FineTuneStatus } from "@/components/atomic-crm/misc/FineTuneStatus";

const supabase = getSupabaseClient();

interface ConvRow {
  created_at: string;
  appointment_booked: boolean | null;
  sentiment: string | null;
}

interface ApptRow {
  status: string | null;
}

const SENTIMENT_COLORS: Record<string, string> = {
  satisfied: "#16A34A",
  neutral: "#6B7280",
  frustrated: "#DC2626",
  confused: "#D97706",
  unknown: "#9CA3AF",
};

function buildDailyBooking(convs: ConvRow[], days: number) {
  const result: {
    date: string;
    total: number;
    booked: number;
    rate: number;
  }[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().split("T")[0];
    const label = d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const dayConvs = convs.filter(
      (c) => c.created_at.split("T")[0] === dateStr,
    );
    const total = dayConvs.length;
    const booked = dayConvs.filter((c) => c.appointment_booked).length;
    result.push({
      date: label,
      total,
      booked,
      rate: total > 0 ? Math.round((booked / total) * 100) : 0,
    });
  }
  return result;
}

function buildSentimentPie(convs: ConvRow[]) {
  const counts: Record<string, number> = {};
  convs.forEach((c) => {
    const s = c.sentiment?.toLowerCase() ?? "unknown";
    counts[s] = (counts[s] ?? 0) + 1;
  });
  return Object.entries(counts).map(([name, value]) => ({ name, value }));
}

function buildNoShowBar(apts: ApptRow[]) {
  const buckets: Record<string, number> = {
    scheduled: 0,
    completed: 0,
    no_show: 0,
    rescheduled: 0,
    cancelled: 0,
  };
  apts.forEach((a) => {
    const s = a.status?.toLowerCase() ?? "scheduled";
    if (s in buckets) buckets[s]++;
  });
  return Object.entries(buckets).map(([status, count]) => ({
    status: status.replace("_", " "),
    count,
  }));
}

const CustomTip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E7EB",
        borderRadius: 8,
        padding: "10px 14px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        fontSize: 12,
      }}
    >
      <p style={{ fontWeight: 600, marginBottom: 4, color: "#111827" }}>
        {label}
      </p>
      {payload.map((p: any) => (
        <div
          key={p.dataKey ?? p.name}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 2,
          }}
        >
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: p.fill ?? p.stroke,
              display: "inline-block",
            }}
          />
          <span style={{ color: "#6B7280", textTransform: "capitalize" }}>
            {p.name ?? p.dataKey}
          </span>
          <span
            style={{
              fontWeight: 700,
              marginLeft: "auto",
              paddingLeft: 16,
              color: "#111827",
            }}
          >
            {p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const StatCard = ({
  label,
  value,
  trend,
  sub,
}: {
  label: string;
  value: string;
  trend?: number;
  sub?: string;
}) => (
  <div
    style={{
      background: "#FFFFFF",
      border: "1px solid #F3F4F6",
      borderRadius: 12,
      padding: "20px 22px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)",
    }}
  >
    <p
      style={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: "#9CA3AF",
        marginBottom: 10,
      }}
    >
      {label}
    </p>
    <p
      style={{
        fontSize: 30,
        fontWeight: 700,
        letterSpacing: "-0.02em",
        color: "#111827",
        lineHeight: 1,
        fontVariantNumeric: "tabular-nums",
        marginBottom: 8,
      }}
    >
      {value}
    </p>
    {(trend !== undefined || sub) && (
      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
        {trend !== undefined && trend !== 0 && (
          <>
            {trend > 0 ? (
              <ArrowUp style={{ width: 11, height: 11, color: "#16A34A" }} />
            ) : (
              <ArrowDown style={{ width: 11, height: 11, color: "#DC2626" }} />
            )}
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: trend > 0 ? "#16A34A" : "#DC2626",
              }}
            >
              {Math.abs(trend)}%
            </span>
          </>
        )}
        {sub && <span style={{ fontSize: 12, color: "#9CA3AF" }}>{sub}</span>}
      </div>
    )}
  </div>
);

const ROIInput = ({
  label,
  value,
  prefix,
  suffix,
  onChange,
}: {
  label: string;
  value: number;
  prefix?: string;
  suffix?: string;
  onChange: (v: number) => void;
}) => (
  <div style={{ flex: 1 }}>
    <p
      style={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: "#6B7280",
        marginBottom: 8,
      }}
    >
      {label}
    </p>
    <div
      style={{ display: "flex", alignItems: "center", position: "relative" }}
    >
      {prefix && (
        <span
          style={{
            position: "absolute",
            left: 12,
            fontSize: 14,
            fontWeight: 600,
            color: "#6B7280",
          }}
        >
          {prefix}
        </span>
      )}
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        style={{
          width: "100%",
          background: "#FFFFFF",
          border: "1px solid #D1D5DB",
          borderRadius: 8,
          padding: prefix ? "8px 12px 8px 24px" : "8px 12px",
          fontSize: 15,
          fontWeight: 700,
          color: "#111827",
          outline: "none",
          textAlign: "center",
          fontVariantNumeric: "tabular-nums",
          boxSizing: "border-box",
        }}
        onFocus={(e) => {
          (e.currentTarget as HTMLElement).style.borderColor = "#16A34A";
        }}
        onBlur={(e) => {
          (e.currentTarget as HTMLElement).style.borderColor = "#D1D5DB";
        }}
      />
      {suffix && (
        <span
          style={{
            position: "absolute",
            right: 12,
            fontSize: 14,
            fontWeight: 600,
            color: "#6B7280",
          }}
        >
          {suffix}
        </span>
      )}
    </div>
  </div>
);

export const AnalyticsPage = () => {
  const [convs, setConvs] = useState<ConvRow[]>([]);
  const [apts, setApts] = useState<ApptRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<7 | 30>(30);
  const [roiMissed, setRoiMissed] = useState(40);
  const [roiValue, setRoiValue] = useState(150);
  const [roiShowUp, setRoiShowUp] = useState(80);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      const [{ data: cd }, { data: ad }] = await Promise.all([
        supabase
          .from("conversations")
          .select("created_at,appointment_booked,sentiment")
          .order("created_at", { ascending: false })
          .limit(1000),
        supabase.from("appointments").select("status").limit(1000),
      ]);
      setConvs((cd as ConvRow[]) ?? []);
      setApts((ad as ApptRow[]) ?? []);
      setLoading(false);
    };
    run();
  }, []);

  const daily = buildDailyBooking(convs, range);
  const sentimentPie = buildSentimentPie(convs);
  const noShowBar = buildNoShowBar(apts);

  const totalCalls = convs.length;
  const totalBooked = convs.filter((c) => c.appointment_booked).length;
  const overallRate = totalCalls
    ? Math.round((totalBooked / totalCalls) * 100)
    : 0;
  const noShowCount = apts.filter((a) => a.status === "no_show").length;
  const noShowRate = apts.length
    ? Math.round((noShowCount / apts.length) * 100)
    : 0;

  const now = new Date();
  const thisMonthStart = new Date(
    now.getFullYear(),
    now.getMonth(),
    1,
  ).toISOString();
  const lastMonthStart = new Date(
    now.getFullYear(),
    now.getMonth() - 1,
    1,
  ).toISOString();
  const lastMonthEnd = new Date(
    now.getFullYear(),
    now.getMonth(),
    0,
    23,
    59,
    59,
  ).toISOString();

  const thisMonthConvs = convs.filter((c) => c.created_at >= thisMonthStart);
  const lastMonthConvs = convs.filter(
    (c) => c.created_at >= lastMonthStart && c.created_at <= lastMonthEnd,
  );

  const bookingRateThisMonth = thisMonthConvs.length
    ? Math.round(
        (thisMonthConvs.filter((c) => c.appointment_booked).length /
          thisMonthConvs.length) *
          100,
      )
    : 0;
  const bookingRateLastMonth = lastMonthConvs.length
    ? Math.round(
        (lastMonthConvs.filter((c) => c.appointment_booked).length /
          lastMonthConvs.length) *
          100,
      )
    : 0;
  const bookingRateTrend = bookingRateLastMonth
    ? Math.round(
        ((bookingRateThisMonth - bookingRateLastMonth) / bookingRateLastMonth) *
          100,
      )
    : 0;

  const bookedThisMonth = thisMonthConvs.filter(
    (c) => c.appointment_booked,
  ).length;
  const bookedLastMonth = lastMonthConvs.filter(
    (c) => c.appointment_booked,
  ).length;
  const revenueThisMonth = bookedThisMonth * 150;
  const revenueLastMonth = bookedLastMonth * 150;
  const revenueTrend = revenueLastMonth
    ? Math.round(
        ((revenueThisMonth - revenueLastMonth) / revenueLastMonth) * 100,
      )
    : 0;

  const callsThisMonth = thisMonthConvs.length;
  const callsLastMonth = lastMonthConvs.length;
  const callsTrend = callsLastMonth
    ? Math.round(((callsThisMonth - callsLastMonth) / callsLastMonth) * 100)
    : 0;

  const monthlyRecovered = roiMissed * roiValue * (roiShowUp / 100);
  const annualRecovered = monthlyRecovered * 12;
  const seraMonthlyCost = 297;
  const netAnnualROI = annualRecovered - seraMonthlyCost * 12;

  const fmtMoney = (n: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(n);

  const cardStyle = {
    background: "#FFFFFF",
    border: "1px solid #F3F4F6",
    borderRadius: 12,
    padding: "22px 24px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)",
  } as const;

  return (
    <div style={{ minHeight: "100vh", background: "#F7F7F8" }}>
      {/* Header */}
      <div
        style={{
          background: "#FFFFFF",
          borderBottom: "1px solid #E5E7EB",
          padding: "20px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>
          <h1
            style={{
              color: "#111827",
              fontSize: 20,
              fontWeight: 700,
              letterSpacing: "-0.02em",
            }}
          >
            Analytics
          </h1>
          <p style={{ color: "#9CA3AF", fontSize: 12, marginTop: 2 }}>
            {totalCalls.toLocaleString()} calls · {overallRate}% booking rate ·{" "}
            {noShowRate}% no-show rate
          </p>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            background: "#F9FAFB",
            border: "1px solid #E5E7EB",
            borderRadius: 8,
            padding: 4,
          }}
        >
          {([7, 30] as const).map((d) => (
            <button
              key={d}
              onClick={() => setRange(d)}
              style={{
                fontSize: 12,
                fontWeight: 500,
                padding: "5px 12px",
                borderRadius: 6,
                background: range === d ? "#FFFFFF" : "transparent",
                color: range === d ? "#111827" : "#9CA3AF",
                border:
                  range === d ? "1px solid #E5E7EB" : "1px solid transparent",
                cursor: "pointer",
                boxShadow: range === d ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
              }}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div
        style={{
          padding: "24px 32px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {loading ? (
          <div style={{ padding: "80px 0", textAlign: "center" }}>
            <div
              style={{
                width: 18,
                height: 18,
                border: "2px solid #E5E7EB",
                borderTopColor: "#16A34A",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
                margin: "0 auto",
              }}
            />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <>
            {/* Stat cards */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 12,
              }}
            >
              <StatCard
                label="Booking rate this month"
                value={`${bookingRateThisMonth}%`}
                trend={bookingRateTrend || undefined}
                sub="vs last month"
              />
              <StatCard
                label="Revenue recovered"
                value={fmtMoney(revenueThisMonth)}
                trend={revenueTrend || undefined}
                sub="this month · $150/booking"
              />
              <StatCard
                label="Calls handled"
                value={callsThisMonth.toLocaleString()}
                trend={callsTrend || undefined}
                sub="this month"
              />
            </div>

            {/* Booking rate trend */}
            <div style={cardStyle}>
              <h2
                style={{
                  color: "#111827",
                  fontSize: 14,
                  fontWeight: 600,
                  marginBottom: 2,
                }}
              >
                Booking rate trend
              </h2>
              <p style={{ color: "#9CA3AF", fontSize: 12, marginBottom: 18 }}>
                Daily calls handled vs appointments booked
              </p>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={daily} margin={{ left: -20 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#F3F4F6"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: "#9CA3AF" }}
                    axisLine={false}
                    tickLine={false}
                    interval={range === 30 ? 4 : 0}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#9CA3AF" }}
                    axisLine={false}
                    tickLine={false}
                    width={28}
                    allowDecimals={false}
                  />
                  <Tooltip
                    content={<CustomTip />}
                    cursor={{ fill: "#F9FAFB" }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 11, color: "#6B7280" }}
                    iconType="circle"
                    iconSize={7}
                  />
                  <Line
                    dataKey="total"
                    name="Total calls"
                    stroke="#DBEAFE"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    dataKey="booked"
                    name="Booked"
                    stroke="#16A34A"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              <div style={cardStyle}>
                <h2
                  style={{
                    color: "#111827",
                    fontSize: 14,
                    fontWeight: 600,
                    marginBottom: 2,
                  }}
                >
                  Sentiment breakdown
                </h2>
                <p style={{ color: "#9CA3AF", fontSize: 12, marginBottom: 14 }}>
                  Caller sentiment across all calls
                </p>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={sentimentPie}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={80}
                      dataKey="value"
                    >
                      {sentimentPie.map((entry, i) => (
                        <Cell
                          key={i}
                          fill={
                            SENTIMENT_COLORS[entry.name] ??
                            SENTIMENT_COLORS.unknown
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTip />} />
                    <Legend
                      wrapperStyle={{ fontSize: 11, color: "#6B7280" }}
                      iconType="circle"
                      iconSize={7}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div style={cardStyle}>
                <h2
                  style={{
                    color: "#111827",
                    fontSize: 14,
                    fontWeight: 600,
                    marginBottom: 2,
                  }}
                >
                  Appointment outcomes
                </h2>
                <p style={{ color: "#9CA3AF", fontSize: 12, marginBottom: 14 }}>
                  Status breakdown across all appointments
                </p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={noShowBar}
                    margin={{ left: -20 }}
                    barSize={28}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#F3F4F6"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="status"
                      tick={{ fontSize: 10, fill: "#9CA3AF" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: "#9CA3AF" }}
                      axisLine={false}
                      tickLine={false}
                      width={28}
                      allowDecimals={false}
                    />
                    <Tooltip
                      content={<CustomTip />}
                      cursor={{ fill: "#F9FAFB" }}
                    />
                    <Bar
                      dataKey="count"
                      name="Appointments"
                      radius={[4, 4, 0, 0]}
                    >
                      {noShowBar.map((entry, i) => (
                        <Cell
                          key={i}
                          fill={
                            entry.status === "no show"
                              ? "#FCA5A5"
                              : entry.status === "completed"
                                ? "#86EFAC"
                                : "#E5E7EB"
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <FineTuneStatus />

            {/* ROI Calculator */}
            <div style={cardStyle}>
              <h2
                style={{
                  color: "#111827",
                  fontSize: 15,
                  fontWeight: 600,
                  marginBottom: 2,
                }}
              >
                ROI Calculator
              </h2>
              <p style={{ color: "#9CA3AF", fontSize: 13, marginBottom: 22 }}>
                See how much Sera pays for itself every year.
              </p>

              <div style={{ display: "flex", gap: 14, marginBottom: 24 }}>
                <ROIInput
                  label="Missed calls / month"
                  value={roiMissed}
                  onChange={setRoiMissed}
                />
                <ROIInput
                  label="Avg. appointment value"
                  value={roiValue}
                  prefix="$"
                  onChange={setRoiValue}
                />
                <ROIInput
                  label="Show-up rate"
                  value={roiShowUp}
                  suffix="%"
                  onChange={setRoiShowUp}
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1px 1fr 1px 1fr 1px 1fr",
                  background: "#F9FAFB",
                  border: "1px solid #E5E7EB",
                  borderRadius: 10,
                  overflow: "hidden",
                }}
              >
                {[
                  {
                    label: "Monthly recovered",
                    value: fmtMoney(monthlyRecovered),
                    color: "#111827",
                  },
                  null,
                  {
                    label: "Annual recovered",
                    value: fmtMoney(annualRecovered),
                    color: "#111827",
                  },
                  null,
                  {
                    label: "Sera annual cost",
                    value: fmtMoney(seraMonthlyCost * 12),
                    color: "#6B7280",
                  },
                  null,
                  {
                    label: "Net annual ROI",
                    value: fmtMoney(netAnnualROI),
                    color: netAnnualROI >= 0 ? "#16A34A" : "#DC2626",
                    large: true,
                  },
                ].map((item, i) =>
                  item === null ? (
                    <div key={i} style={{ background: "#E5E7EB" }} />
                  ) : (
                    <div
                      key={i}
                      style={{ padding: "16px 18px", background: "#FFFFFF" }}
                    >
                      <p
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: "#9CA3AF",
                          marginBottom: 6,
                        }}
                      >
                        {item.label}
                      </p>
                      <p
                        style={{
                          fontSize: item.large ? 24 : 18,
                          fontWeight: 700,
                          color: item.color,
                          letterSpacing: "-0.02em",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {item.value}
                      </p>
                    </div>
                  ),
                )}
              </div>
              <p style={{ fontSize: 12, color: "#9CA3AF", marginTop: 10 }}>
                This is how much Sera pays for itself every year.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
